import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ─── Counterparty tier labels / colors ──────────────────────────────────────

export const CP_TIER_LABELS = {
  cp_1: 'CP-1 Critical',
  cp_2: 'CP-2 Professional',
  cp_3: 'CP-3 Operational',
  cp_4: 'CP-4 Internal',
}

export const CP_TIER_COLORS = {
  cp_1: 'bg-red-100 text-red-700 border-red-200',
  cp_2: 'bg-amber-100 text-amber-700 border-amber-200',
  cp_3: 'bg-blue-100 text-blue-700 border-blue-200',
  cp_4: 'bg-green-100 text-green-700 border-green-200',
}

export const CP_TIER_DESCRIPTIONS = {
  cp_1: 'Studio/network execs, attorneys, active deal counterparties, clients — draft only, never autonomous',
  cp_2: 'Agents, managers, producers — reviewed send maximum',
  cp_3: 'Vendors, service providers, general industry — policy-bound autonomous allowed',
  cp_4: 'Chatrone team — internal mode only',
}

// ─── System state hook ───────────────────────────────────────────────────────

export function useJanetSystemState() {
  const [state, setState]   = useState(null)   // { janet_enabled, status, status_reason, changed_by_name, changed_at }
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error: err } = await supabase
        .from('va_system_state')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      if (err && err.code !== 'PGRST116') throw err
      setState(data || null)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])

  // Realtime — the state table is append-only: every kill-switch flip is a
  // new INSERT, and the "current" state is just the most-recent row. When
  // any authorized user flips the switch from their own machine, every
  // other open tab's Janet UI must reflect it instantly (per VA-SAFETY-v0.1
  // §9: "silent VA is always preferable to a misbehaving VA").
  //
  // Requires va_system_state in the supabase_realtime publication — see
  // docs/specs/REALTIME-1-IMPLEMENTATION.md §1 Step 3. Without the Dashboard
  // toggle this is a no-op and the UI only reflects flips on reload.
  useEffect(() => {
    const channel = supabase
      .channel('va-system-state')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'va_system_state' }, () => {
        fetch()
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [fetch])

  return { state, loading, error, refetch: fetch }
}

// ─── Kill switch ─────────────────────────────────────────────────────────────

export async function triggerKillSwitch({ userId, userName, reason }) {
  const { error } = await supabase
    .from('va_system_state')
    .insert([{
      janet_enabled:   false,
      status:          'killed',
      status_reason:   reason || 'Kill switch activated',
      changed_by_id:   userId   || null,
      changed_by_name: userName || 'Unknown',
    }])
  if (error) throw error
}

export async function enableJanet({ userId, userName, reason }) {
  const { error } = await supabase
    .from('va_system_state')
    .insert([{
      janet_enabled:   true,
      status:          'active',
      status_reason:   reason || 'Manually enabled',
      changed_by_id:   userId   || null,
      changed_by_name: userName || 'Unknown',
    }])
  if (error) throw error
}

export async function pauseJanet({ userId, userName, reason }) {
  const { error } = await supabase
    .from('va_system_state')
    .insert([{
      janet_enabled:   false,
      status:          'paused',
      status_reason:   reason || 'Manually paused',
      changed_by_id:   userId   || null,
      changed_by_name: userName || 'Unknown',
    }])
  if (error) throw error
}

// ─── State history ────────────────────────────────────────────────────────────

export function useJanetStateHistory() {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from('va_system_state')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setHistory(data || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return { history, loading }
}

// ─── Decision log ─────────────────────────────────────────────────────────────

export async function logJanetDecision(entry) {
  const payload = {
    principal_id:         entry.principalId         || null,
    principal_name:       entry.principalName        || null,
    mode:                 entry.mode                 || 'internal',
    authority_tier:       entry.authorityTier        || 'T0',
    cp_tier:              entry.cpTier               || null,
    recipient_contact_id: entry.recipientContactId   || null,
    recipient_email:      entry.recipientEmail       || null,
    model_version:        entry.modelVersion         || null,
    prompt_version_hash:  entry.promptVersionHash    || null,
    input_context:        entry.inputContext         || {},
    reasoning_trace:      entry.reasoningTrace       || null,
    proposed_action:      entry.proposedAction       || {},
    scanner_results:      entry.scannerResults       || {},
    final_action:         entry.finalAction          || 'suppress',
    human_approver_id:    entry.humanApproverId      || null,
    human_approver_name:  entry.humanApproverName    || null,
    company_id:           entry.companyId            || 'chatrone_us',
  }
  const { data, error } = await supabase
    .from('va_decision_log')
    .insert([payload])
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

// ─── Incidents ────────────────────────────────────────────────────────────────

export function useJanetIncidents() {
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading]     = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('va_incidents')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100)
    setIncidents(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetch() }, [fetch])

  return { incidents, loading, refetch: fetch }
}

export async function createJanetIncident(data) {
  const { data: row, error } = await supabase
    .from('va_incidents')
    .insert([{
      severity:            data.severity,
      title:               data.title,
      description:         data.description,
      affected_capability: data.affectedCapability || null,
      cp_tier:             data.cpTier             || null,
      decision_log_id:     data.decisionLogId      || null,
      detection_method:    data.detectionMethod    || 'manual',
      company_id:          data.companyId          || 'chatrone_us',
    }])
    .select()
    .single()
  if (error) throw error
  return row
}

// ─── Contact CP tier helpers ──────────────────────────────────────────────────

export async function setContactCpTier(contactId, cpTier) {
  const { error } = await supabase
    .from('contacts')
    .update({ va_cp_tier: cpTier || null })
    .eq('id', contactId)
  if (error) throw error
}

export async function setContactVipBypass(contactId, bypass) {
  const { error } = await supabase
    .from('contacts')
    .update({ va_vip_bypass: bypass })
    .eq('id', contactId)
  if (error) throw error
}

// ─── Check if Janet is currently active (lightweight poll) ───────────────────

export async function isJanetActive() {
  const { data } = await supabase
    .from('va_system_state')
    .select('janet_enabled, status')
    .order('created_at', { ascending: false })
    .limit(1)
    .single()
  return data?.janet_enabled === true && data?.status === 'active'
}
